import { useState, useEffect, useRef } from 'react';
import { MapPin, Loader2, CheckCircle2 } from 'lucide-react';
import { UseFormRegister, UseFormSetValue, UseFormWatch } from 'react-hook-form';

import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import AddressSelector from '@/components/common/AddressSelector';
import { useUIStore } from '@/store/uiStore';
import { useProvinces, useDistricts, useWards } from '@/hooks/useAddress';
import { addressApi } from '@/api/addressApi';

export interface GeoLocation {
  lat: string;
  lon: string;
  display_name: string;
}

interface LocationPickerProps {
  countryField?: string;
  provinceField?: string;
  districtField?: string;
  wardField?: string;
  addressField: string;

  register: UseFormRegister<any>;
  setValue: UseFormSetValue<any>;
  watch: UseFormWatch<any>;
  errors: any;

  selectedGeo: GeoLocation | null;
  onSelectGeo: (geo: GeoLocation | null) => void;

  addressLabel?: string;
  addressPlaceholder?: string;
}

export default function LocationPicker({
  countryField = 'countryId',
  provinceField = 'provinceId',
  districtField = 'districtId',
  wardField = 'wardId',
  addressField,
  register,
  setValue,
  watch,
  errors,
  selectedGeo,
  onSelectGeo,
  addressLabel = 'Địa chỉ cụ thể *',
  addressPlaceholder = 'Số nhà, tên đường...',
}: LocationPickerProps) {
  const { addToast } = useUIStore();

  const countryId = watch(countryField) || 1;
  const provinceId = watch(provinceField);
  const districtId = watch(districtField);
  const wardId = watch(wardField);
  const addressString = watch(addressField);

  const { data: provinces } = useProvinces(countryId);
  const { data: districts } = useDistricts(provinceId);
  const { data: wards } = useWards(districtId);

  const [geoResults, setGeoResults] = useState<any[]>([]);
  const [isSearchingGeo, setIsSearchingGeo] = useState(false);
  const [geoSearchQuery, setGeoSearchQuery] = useState('');
  const mapInstanceRef = useRef<any>(null);
  const markerRef = useRef<any>(null);
  const isAutoFilling = useRef(false);

  const isMapVisible = !!addressString && !!countryId && !!provinceId && !!districtId && !!wardId;

  useEffect(() => {
    if (!isMapVisible) {
      if (mapInstanceRef.current) {
        mapInstanceRef.current.remove();
        mapInstanceRef.current = null;
      }
      return;
    }

    const loadCss = (id: string, href: string) => {
      if (!document.getElementById(id)) {
        const link = document.createElement('link');
        link.id = id; link.rel = 'stylesheet'; link.href = href;
        document.head.appendChild(link);
      }
    };
    loadCss('leaflet-css', 'https://unpkg.com/leaflet@1.9.4/dist/leaflet.css');

    const loadScript = (src: string) => {
      return new Promise((resolve) => {
        if ((window as any).L) return resolve(true);
        const existingScript = document.querySelector(`script[src="${src}"]`) as HTMLScriptElement;
        if (existingScript) {
          if (existingScript.getAttribute('data-loaded') === 'true') return resolve(true);
          existingScript.addEventListener('load', () => resolve(true));
          return;
        }
        const script = document.createElement('script');
        script.src = src;
        script.onload = () => {
          script.setAttribute('data-loaded', 'true');
          resolve(true);
        };
        document.head.appendChild(script);
      });
    };

    const initMap = async () => {
      if (!(window as any).L) {
        await loadScript('https://unpkg.com/leaflet@1.9.4/dist/leaflet.js');
      }

      requestAnimationFrame(() => {
        setTimeout(() => {
          const L = (window as any).L;
          const mapDiv = document.getElementById('geo-picker-map');
          if (!mapDiv || mapInstanceRef.current) return;
          const map = L.map(mapDiv, { zoomControl: true }).setView([10.7769, 106.7009], 12);

          L.tileLayer('https://mt1.google.com/vt/lyrs=m&x={x}&y={y}&z={z}', {
            attribution: '© Google Maps',
            maxZoom: 20,
          }).addTo(map);

          mapInstanceRef.current = map;

          if (selectedGeo) {
            const lat = parseFloat(selectedGeo.lat);
            const lon = parseFloat(selectedGeo.lon);
            if (!isNaN(lat) && !isNaN(lon)) {
              map.setView([lat, lon], 16);
              if (markerRef.current) map.removeLayer(markerRef.current);
              markerRef.current = L.marker([lat, lon]).addTo(map);
              markerRef.current.bindPopup(selectedGeo.display_name).openPopup();
            }
          }
        }, 100);
      });
    };

    initMap();
  }, [isMapVisible]); // Only re-init if visibility changes

  useEffect(() => {
    if (selectedGeo && mapInstanceRef.current && (window as any).L) {
      const L = (window as any).L;
      const map = mapInstanceRef.current;
      const lat = parseFloat(selectedGeo.lat);
      const lon = parseFloat(selectedGeo.lon);

      if (!isNaN(lat) && !isNaN(lon)) {
        map.setView([lat, lon], 16);
        if (markerRef.current) {
          map.removeLayer(markerRef.current);
        }
        markerRef.current = L.marker([lat, lon]).addTo(map);
        markerRef.current.bindPopup(selectedGeo.display_name).openPopup();
      }
    }
  }, [selectedGeo]);

  useEffect(() => {
    if (isAutoFilling.current) return;
    const provinceName = provinces?.find((p) => p.id === provinceId)?.name || '';
    const districtName = districts?.find((d) => d.id === districtId)?.name || '';
    const wardName = wards?.find((w) => w.id === wardId)?.name || '';
    const fullAddress = [addressString, wardName, districtName, provinceName, 'Vietnam']
      .filter(Boolean)
      .join(', ');

    setGeoSearchQuery(fullAddress);
  }, [provinceId, districtId, wardId, addressString, provinces, districts, wards]);

  const handleManualSearchGeo = async () => {
    if (!geoSearchQuery.trim()) {
      addToast('error', 'Vui lòng nhập địa chỉ để tìm kiếm');
      return;
    }

    setIsSearchingGeo(true);
    setGeoResults([]);
    onSelectGeo(null);

    try {
      const res = await fetch(`https://nominatim.openstreetmap.org/search?format=json&addressdetails=1&q=${encodeURIComponent(geoSearchQuery)}`, {
        headers: {
          'Accept-Language': 'vi-VN,vi;q=0.9,en-US;q=0.8,en;q=0.7',
        }
      });
      const data = await res.json();

      if (data && data.length > 0) {
        setGeoResults(data);
      } else {
        addToast('error', 'Không tìm thấy địa điểm nào phù hợp.');
      }
    } catch (err) {
      addToast('error', 'Lỗi khi tìm tọa độ.');
    } finally {
      setIsSearchingGeo(false);
    }
  };

  const handleSelectGeo = async (geo: any) => {
    onSelectGeo({ lat: geo.lat, lon: geo.lon, display_name: geo.display_name });

    if (!geo.address) return;

    isAutoFilling.current = true;

    try {
      const norm = (s?: string) => s?.toLowerCase().replace(/(thành phố|tỉnh|quận|huyện|thị xã|phường|xã|thị trấn|tp\.)\s*/ig, '').trim() || '';

      const geoProv = norm(geo.address.state || geo.address.city || geo.address.town || geo.address.county);
      const geoDist = norm(geo.address.city_district || geo.address.county || geo.address.suburb || geo.address.city || geo.address.town);
      const geoWard = norm(geo.address.suburb || geo.address.village || geo.address.neighbourhood || geo.address.quarter || geo.address.hamlet);
      const geoStreet = geo.address.road || '';

      if (geoStreet && !watch(addressField)) {
        const houseNumber = geo.address.house_number ? `${geo.address.house_number}, ` : '';
        setValue(addressField, houseNumber + geoStreet, { shouldValidate: true });
      }

      if (provinces && provinces.length > 0) {
        const matchedProv = provinces.find(p => {
          const pNorm = norm(p.name);
          return pNorm === geoProv || pNorm.includes(geoProv) || geoProv.includes(pNorm);
        });

        if (matchedProv) {
          setValue(provinceField, matchedProv.id, { shouldValidate: true });

          const dists = await addressApi.getDistricts(matchedProv.id);
          const matchedDist = dists.find(d => {
            const dNorm = norm(d.name);
            return dNorm === geoDist || dNorm.includes(geoDist) || geoDist.includes(dNorm);
          });

          if (matchedDist) {
            setValue(districtField, matchedDist.id, { shouldValidate: true });

            const wrds = await addressApi.getWards(matchedDist.id);
            const matchedWard = wrds.find(w => {
              const wNorm = norm(w.name);
              return wNorm === geoWard || wNorm.includes(geoWard) || geoWard.includes(wNorm);
            });

            if (matchedWard) {
              setValue(wardField, matchedWard.id, { shouldValidate: true });
            }
          }
        }
      }
    } catch (e) {
      console.log('Auto-fill district/ward failed', e);
    } finally {
      setTimeout(() => { isAutoFilling.current = false; }, 500);
    }
  };



  return (
    <>


      <AddressSelector
        required
        countryId={countryId}
        provinceId={provinceId}
        districtId={districtId}
        wardId={wardId}
        onCountryChange={(id) => {
          setValue(countryField, id);
          setValue(provinceField, 0);
          setValue(districtField, 0);
          setValue(wardField, 0);
        }}
        onProvinceChange={(id) => {
          setValue(provinceField, id);
          setValue(districtField, 0);
          setValue(wardField, 0);
        }}
        onDistrictChange={(id) => {
          setValue(districtField, id);
          setValue(wardField, 0);
        }}
        onWardChange={(id) => setValue(wardField, id)}
        errors={{
          countryId: errors[countryField]?.message,
          provinceId: errors[provinceField]?.message,
          districtId: errors[districtField]?.message,
          wardId: errors[wardField]?.message,
        }}
      />

      <div className={"mt-4"}>
        <Input
          label={addressLabel}
          placeholder={addressPlaceholder}
          error={errors[addressField]?.message}
          {...register(addressField)}
        />
      </div>

      {isMapVisible && (
        <div className="mt-6 pt-6 border-t" style={{ borderColor: 'var(--border-c)' }}>
          <div>
            <p className="text-sm font-bold" style={{ color: 'var(--text-1)' }}>Xác nhận tọa độ bản đồ</p>
            <p className="text-xs mt-0.5 mb-4" style={{ color: 'var(--text-3)' }}>
              Địa chỉ của bạn sẽ được tự động tìm kiếm trên bản đồ. Vui lòng chọn tọa độ chính xác nhất.
            </p>
          </div>

          <div className="flex flex-col gap-4">
            <div className="flex gap-2">
              <Input
                value={geoSearchQuery}
                onChange={(e) => setGeoSearchQuery(e.target.value)}
                placeholder="Nhập địa chỉ cần tìm..."
                className="flex-1"
              />
              <Button type="button" variant="brand" onClick={handleManualSearchGeo} disabled={isSearchingGeo}>
                {isSearchingGeo ? <Loader2 className="animate-spin w-4 h-4" /> : 'Tìm'}
              </Button>
            </div>

            <div className="flex flex-col md:flex-row gap-4 h-[360px]">
              <div className="w-full md:w-4/12 flex flex-col h-full overflow-y-auto space-y-2 pr-1 custom-scrollbar">
                {geoResults.length > 0 ? (
                  geoResults.map((geo, idx) => (
                    <div
                      key={idx}
                      className={cn(
                        'p-3 rounded-lg border text-sm cursor-pointer transition-colors shrink-0',
                        selectedGeo?.lat === geo.lat && selectedGeo?.lon === geo.lon
                          ? 'border-orange-500 bg-orange-50/50'
                          : 'border-gray-200 hover:border-orange-300 bg-white'
                      )}
                      onClick={() => handleSelectGeo(geo)}
                    >
                      <div className="flex gap-2">
                        <MapPin
                          className={cn(
                            'w-4 h-4 mt-0.5 shrink-0',
                            selectedGeo?.lat === geo.lat && selectedGeo?.lon === geo.lon
                              ? 'text-orange-500'
                              : 'text-gray-400'
                          )}
                        />
                        <span className="text-gray-700">{geo.display_name}</span>
                      </div>
                    </div>
                  ))
                ) : (
                  <div className="h-full flex flex-col items-center justify-center text-gray-400 border rounded-lg border-dashed">
                    <MapPin size={32} className="mb-2 opacity-20" />
                    <p className="text-sm">Chưa có dữ liệu</p>
                  </div>
                )}
              </div>

              <div
                className="w-full md:w-8/12 h-full rounded-xl border overflow-hidden relative"
                style={{ borderColor: 'var(--border-c)', background: '#e5e7eb' }}
              >
                <div id="geo-picker-map" className="w-full h-full z-0" />

                {selectedGeo && (
                  <div className="absolute bottom-3 left-3 right-3 z-[1000] p-3 bg-white/90 backdrop-blur text-green-700 rounded-lg text-sm flex items-center gap-2 border border-green-200 shadow-lg">
                    <CheckCircle2 size={16} className="text-green-600 shrink-0" />
                    <span className="truncate">
                      Đã chọn: {Number(selectedGeo.lat).toFixed(4)}, {Number(selectedGeo.lon).toFixed(4)}
                    </span>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
